"use client";

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { NotificationIcon } from "@/components/appIcons";
import { Link } from "@/components/link";
import { useIsInactiveFor } from "@/hooks/use-is-inactive-for";
import { useMediaQuery } from "@/hooks/use-media-query";
import { useViewerQueryState } from "@/hooks/use-viewer-query-state";
import { syncAppBadge } from "@/lib/syncAppBadge";
import { QUERY_IDLE_TIMEOUT_MS, QUERY_STALE_TIMES } from "@/utils/query-policies";
import { readStoredQueryData, writeStoredQueryData } from "@/utils/query-storage";

import { NotificationDropdown } from "./notification-dropdown";
import {
  getUnreadNotificationsCountQueryOptions,
  getUnreadNotificationsStorageKey,
  type UnreadNotificationsCount,
} from "./unread-count-query";

export function NotificationBell() {
  const { session, enabledWhenAuthenticated } = useViewerQueryState();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const hasHydrated = useHasHydrated();
  const isInactive = useIsInactiveFor(QUERY_IDLE_TIMEOUT_MS);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const unreadCountStorageKey = session ? getUnreadNotificationsStorageKey(session.user.id) : null;

  useEffect(() => {
    if (!session) syncAppBadge(0);
  }, [session]);

  useEffect(() => {
    if (!dropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  if (!session) return null;

  const badge = hasHydrated && unreadCountStorageKey && (
    <NotificationUnreadBadge
      storageKey={unreadCountStorageKey}
      enabled={enabledWhenAuthenticated && !isInactive}
    />
  );

  if (!isDesktop) {
    return (
      <Link
        to={"/notifications"}
        className="relative flex items-center justify-center size-9 rounded-lg hover:bg-secondary transition-colors after:absolute after:-inset-1 after:content-['']"
      >
        <NotificationIcon className="text-[22px]" />
        {badge}
      </Link>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setDropdownOpen((prev) => !prev)}
        className="relative flex items-center justify-center size-9 rounded-lg hover:bg-secondary transition-colors after:absolute after:-inset-1 after:content-['']"
      >
        <NotificationIcon className="text-[22px]" />
        {badge}
      </button>

      {dropdownOpen && <NotificationDropdown onClose={() => setDropdownOpen(false)} />}
    </div>
  );
}

function useHasHydrated() {
  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  return hasHydrated;
}

function NotificationUnreadBadge({
  enabled,
  storageKey,
}: {
  enabled: boolean;
  storageKey: string;
}) {
  const storedUnreadCount = useMemo(
    () => readStoredQueryData<UnreadNotificationsCount>(storageKey, QUERY_STALE_TIMES.unreadCount),
    [storageKey],
  );

  const { data } = useQuery({
    ...getUnreadNotificationsCountQueryOptions(),
    enabled,
    initialData: storedUnreadCount?.data,
    initialDataUpdatedAt: storedUnreadCount?.cachedAt,
  });

  useEffect(() => {
    if (!data) return;
    writeStoredQueryData(storageKey, data);
  }, [data, storageKey]);

  const unreadCount = data?.count ?? 0;

  useEffect(() => {
    syncAppBadge(unreadCount);
  }, [unreadCount]);

  if (unreadCount <= 0) return null;

  return (
    <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-destructive rounded-full">
      {unreadCount > 99 ? "99+" : unreadCount}
    </span>
  );
}
